import React, { FC, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { IGoodsShort } from 'common/models';
import { ArrowLeftLong, ArrowRightLong } from 'common/svg';
import { GoodAnalogCard } from 'entities/Goods/components';

interface IComponentProps {
  analogues?: IGoodsShort[];
  loading: boolean;
  onAddToBasketClick: (id: number, count: number) => void;
  onNavigateToBasketClick: () => void;
}

const styles = {
  scrollBtn: {
    top: '50%',
    borderRadius: '50%',
    boxShadow: '0px 4px 12px 0px rgba(57, 57, 57, 0.2)',
    zIndex: 1,
  },
};

export const GoodAnalogues: FC<IComponentProps> = ({ analogues = [], loading, onAddToBasketClick, onNavigateToBasketClick }) => {
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;

    if (!scrollContainer) {
      return;
    }

    update();

    scrollContainer.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      scrollContainer.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const update = (): void => {
    const scrollContainer = scrollRef.current;

    if (!scrollContainer) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
  };

  const scrollBy = (direction: 'left' | 'right'): void => {
    const scrollContainer = scrollRef.current;

    if (!scrollContainer) {
      return;
    }

    const amount = scrollContainer.clientWidth * 0.8;

    scrollContainer.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  if (!analogues.length) {
    return null;
  }

  return (
    <div className="relative">
      {canScrollLeft && (
        <div className="absolute" style={{ ...styles.scrollBtn, left: 0, transform: 'translate(-22px, -50%)' }}>
          <Button className="button-circle secondary" icon={<ArrowLeftLong />} onClick={() => scrollBy('left')} />
        </div>
      )}

      <div className="good-analogues scrollbar-none" ref={scrollRef}>
        {analogues.map((item) => {
          return (
            <GoodAnalogCard
              key={item.id}
              analog={item}
              loading={loading}
              onAddToBasketClick={onAddToBasketClick}
              onNavigateToBasketClick={onNavigateToBasketClick}
            />
          );
        })}
      </div>

      {canScrollRight && (
        <div className="absolute" style={{ ...styles.scrollBtn, right: 0, transform: 'translate(22px, -50%)' }}>
          <Button className="button-circle secondary" icon={<ArrowRightLong />} onClick={() => scrollBy('right')} />
        </div>
      )}
    </div>
  );
};
